/**
 * お気に入り（ブラウザの localStorage に商品 handle を保存）
 *
 * - [data-tata-favorite][data-product-handle] のボタンを押すと追加 / 解除
 *   （商品カードのハート、商品詳細の「お気に入りに追加」）
 * - [data-tata-favorite-count] に件数を data 属性で反映（ヘッダーの FAVORITE(n)）
 * - [data-tata-favorites-page] があればお気に入り一覧を描画（page.favorites）
 */
(() => {
  const STORAGE_KEY = 'tata:favorites';

  const read = () => {
    try {
      const value = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '[]');
      return Array.isArray(value) ? value.filter((handle) => typeof handle === 'string' && handle) : [];
    } catch {
      return [];
    }
  };

  const write = (list) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    } catch {
      /* プライベートブラウズ等で保存できない場合はページ内だけで保持 */
    }
  };

  let favorites = read();
  const has = (handle) => favorites.includes(handle);

  const syncButtons = (root = document) => {
    root.querySelectorAll('[data-tata-favorite]').forEach((button) => {
      const active = has(button.dataset.productHandle);
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', String(active));
      const label = button.querySelector('[data-tata-favorite-label]');
      if (label) label.textContent = active ? button.dataset.labelRemove : button.dataset.labelAdd;
      const aria = active ? button.dataset.ariaRemove : button.dataset.ariaAdd;
      if (aria) button.setAttribute('aria-label', aria);
    });
    document.querySelectorAll('[data-tata-favorite-count]').forEach((element) => {
      element.setAttribute('data-tata-favorite-count', String(favorites.length));
    });
  };

  const toggle = (handle) => {
    const added = !has(handle);
    favorites = added ? [handle, ...favorites] : favorites.filter((item) => item !== handle);
    write(favorites);
    syncButtons();
    document.dispatchEvent(
      new CustomEvent('tata:favorites:change', { detail: { handle, added, favorites: [...favorites] } })
    );
  };

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-tata-favorite]');
    if (!button) return;
    // 商品カードはカード全体がリンクなので、ハートを押したときは遷移させない
    event.preventDefault();
    event.stopPropagation();
    toggle(button.dataset.productHandle);
  });

  // 別タブでの変更を反映
  window.addEventListener('storage', (event) => {
    if (event.key !== STORAGE_KEY) return;
    favorites = read();
    syncButtons();
    renderPage();
  });

  // クイックビュー・絞り込み・ルック追加読み込みなど後から入るボタンにも状態を反映
  let syncQueued = false;
  new MutationObserver(() => {
    if (syncQueued) return;
    syncQueued = true;
    requestAnimationFrame(() => {
      syncQueued = false;
      syncButtons();
    });
  }).observe(document.documentElement, { childList: true, subtree: true });

  /* ---------- お気に入り一覧ページ ---------- */

  const page = document.querySelector('[data-tata-favorites-page]');
  const grid = page?.querySelector('[data-tata-favorites-grid]');
  const emptyState = page?.querySelector('[data-tata-favorites-empty]');
  const countLabel = page?.querySelector('[data-tata-favorites-count]');
  const cardCache = new Map();
  const GONE = 'gone';

  const fetchCard = async (handle) => {
    if (cardCache.has(handle)) return cardCache.get(handle);
    // 戻り値：カード要素 / GONE（商品が削除・非公開で 404）/ null（通信エラーなど一時的な失敗）
    const request = fetch(`${window.Shopify?.routes?.root || '/'}products/${encodeURIComponent(handle)}?view=favorite-card`)
      .then((response) => {
        if (response.status === 404) return GONE;
        return response.ok ? response.text() : null;
      })
      .then((html) => {
        if (html === GONE || !html) return html;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        return doc.querySelector('.card-wrapper');
      })
      .catch(() => null);
    cardCache.set(handle, request);
    return request;
  };

  const updatePageState = (count) => {
    if (emptyState) emptyState.hidden = count > 0;
    if (grid) grid.hidden = count === 0;
    if (countLabel) {
      countLabel.hidden = count === 0;
      // 「[count]件」を数字と単位に分け、数字を大きく見せる
      const [before = '', after = ''] = (countLabel.dataset.template || '[count]').split('[count]');
      const number = document.createElement('span');
      number.className = 'tata-favorites__count-number';
      number.textContent = count;
      const unit = document.createElement('span');
      unit.className = 'tata-favorites__count-unit';
      unit.textContent = `${before}${after}`.trim();
      countLabel.replaceChildren(number, unit);
    }
  };

  let renderToken = 0;
  async function renderPage() {
    if (!page || !grid) return;
    const token = ++renderToken;
    page.classList.add('is-loading');
    const handles = [...favorites];
    const cards = await Promise.all(handles.map(fetchCard));
    if (token !== renderToken) return;

    // 削除・非公開になった商品は保存からも外し、ヘッダーの件数と一覧の件数を揃える
    // （通信エラーで取得できなかっただけの商品は残す）
    const gone = handles.filter((_, index) => cards[index] === GONE);
    if (gone.length) {
      favorites = favorites.filter((handle) => !gone.includes(handle));
      write(favorites);
      syncButtons();
    }

    const fragment = document.createDocumentFragment();
    let count = 0;
    cards.forEach((card, index) => {
      // 取得できなかった商品は表示しない
      if (!card || card === GONE) return;
      const item = document.createElement('li');
      item.className = 'grid__item tata-favorites__item';
      item.dataset.productHandle = handles[index];
      item.appendChild(document.importNode(card, true));
      fragment.appendChild(item);
      count += 1;
    });
    grid.replaceChildren(fragment);
    updatePageState(count);
    page.classList.remove('is-loading');
    syncButtons(grid);
  }

  if (page) {
    document.addEventListener('tata:favorites:change', (event) => {
      if (event.detail.added) {
        renderPage();
        return;
      }
      // 一覧ページでハートを外したらその商品をフェードアウトして取り除く
      const item = grid?.querySelector(`[data-product-handle="${CSS.escape(event.detail.handle)}"].tata-favorites__item`);
      if (!item) return;
      item.classList.add('is-removing');
      setTimeout(() => {
        item.remove();
        updatePageState(grid.querySelectorAll('.tata-favorites__item').length);
      }, 250);
    });
  }

  window.tataFavorites = {
    list: () => [...favorites],
    has,
    toggle,
  };

  const init = () => {
    syncButtons();
    renderPage();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
